import { TIME_ROWS } from "./secrets.ts";
import type { DayPeriod } from "../world/fx.ts";
import type { SpeciesId } from "./species.ts";

export const TILE = 16;

export type TileId =
  | "void"
  | "floor"
  | "wall"
  | "path"
  | "grass"
  | "tall"
  | "water"
  | "foam"
  | "wood"
  | "roof"
  | "door"
  | "counter"
  | "flower"
  | "reed"
  | "crate"
  | "barrel"
  | "lamp"
  | "rock"
  | "fence"
  | "post"
  | "rest"
  | "tree";

export interface TileDef {
  id: TileId;
  solid: boolean;
  tall?: boolean;
  water?: boolean;
}

export const TILES: Record<TileId, TileDef> = {
  void: { id: "void", solid: true },
  floor: { id: "floor", solid: false },
  wall: { id: "wall", solid: true },
  path: { id: "path", solid: false },
  grass: { id: "grass", solid: false },
  tall: { id: "tall", solid: false, tall: true },
  water: { id: "water", solid: true, water: true },
  foam: { id: "foam", solid: true, water: true },
  wood: { id: "wood", solid: false },
  roof: { id: "roof", solid: true },
  door: { id: "door", solid: false },
  counter: { id: "counter", solid: true },
  flower: { id: "flower", solid: false },
  reed: { id: "reed", solid: true },
  crate: { id: "crate", solid: true },
  barrel: { id: "barrel", solid: true },
  lamp: { id: "lamp", solid: true },
  rock: { id: "rock", solid: true },
  fence: { id: "fence", solid: true },
  post: { id: "post", solid: true },
  rest: { id: "rest", solid: false },
  tree: { id: "tree", solid: true },
};

const LEGEND: Record<string, TileId> = {
  ".": "floor",
  "#": "wall",
  "P": "path",
  ",": "grass",
  "g": "tall",
  "~": "water",
  "f": "foam",
  "w": "wood",
  "R": "roof",
  "D": "door",
  "=": "counter",
  "*": "flower",
  "/": "reed",
  "c": "crate",
  "b": "barrel",
  "l": "lamp",
  "r": "rock",
  "F": "fence",
  "!": "post",
  "K": "rest",
  "T": "tree",
};

/**
 * 140 x 52. Columns 0-43: Stubtown, safe cobble spines, shaped habitats and field rest pads.
 * Columns 44-91: the Frontier -- Frontier Post, Frostfloor, the Crystal Cache, Candle Dunes,
 * Rugmire and Magma Mint -- reached by the north road past Cope Cliffs and the south road past
 * Yield Yard. Rows 30-51: the Undernet -- Neon Harbor, the Mempool Caverns, Neon Grid, Coral
 * Ledger and Cloud Chain -- reached through the gate at the south end of Candle Dunes.
 * Columns 92-139: the Sidechain -- Bridgeport, Halving Harvest, Ghostchain, Pump Jungle, Sakura
 * Stake and the Rugpull Ruins -- reached east past Sysop Vex and Skykeeper Lune, climbing from
 * Bridgeport up through the jungle to the gardens and the ruins.
 */
const RAW = [
  "############################################################################################################################################",
  "#RRRRRRR..bRRRRR########,,,,,,,,,,,,,,,,,,,#.RRRRR.RRRR.TTTTTTTgggggT,TTT,,,,,,,ggggg,,,,,,##,,,,,,,T,,,,T*,,,,,,T,,##,r,,,r,,,r,,,r,,,r,,,#",
  "#RRRRRRR...RRRRR#~~~~/##~~~~~,,rr,,,rrrrrr,#.RRRRR.RRRR.T,ggg,ggggggg,ggg,rrrr,ggggggg,rrr,##,~~~,,,,ggggggggTggg,,,##ggggggggggrrggggggg,,#",
  "#RRRRRRR...RRRRR#~ggg,##~~~~~,rrr,,,rrrrrr,#.RRRRR.RRRR.,ggggggggggggggggg,rrr,ggggggg,rrr,##~~~~~,,,gggTgggggggg,T,##ggggrrggggggggggggg,,#",
  "#RRRRRRR...RRRRR#~gggg##~~~~~,rrr,,,,ggggg,#.RRDRR.RDRR.T,ggg,ggggggg,ggg,rrrr,ggggggg,rrr,##,~~~,,,,gggggggggggg,,,##gggggggggggggggrrgg,,#",
  "#RRRDRRR...RRDRR#Pgggg##ggggg*,,*,,,,ggggg,#l.........!lT!,,,,,ggggg,,,,,T,!,,,,ggggg,,,,,,##*,*,,,,,!,,,*,,,,,,,,,,##!r,,,r,,,r,,,r,,,r,,,#",
  "#.*.PPPPPPPPPPPPPPggggPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP##PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP,#",
  "#..RRR..c...PP,,ggggg,PKPPPPPPPPPKPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP##PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP,#",
  "#..RRR*...*.PP,,gggg,~~#,!PP*,,,!PP*,,,,,,,#.....PP...*.T,,,,,,,,,K,PP,,,,r,gg,,,,K,PP,ggg,##T~,,,,PP,,,,,,,K,,,,,,,##,,,,,,,,K,,,,,,,,,,,,#",
  "#..RDR..l...PP.r..gf~~~#ggPPgggggPP,,gggg,,#..*..PP..*..T,,,,,T,,,,,PP,ggT,ggggg,,,,PP,ggg,##~~~,,,PP,ggggggggggggT,##ggggggggggggggggggg,,#",
  "#.l.PPPPPPPPPP.*..~~~~~#ggPPgggggPPggggg,,,#l....PP....lTr~~~~~,ggg,PPggggrgggggrr,,P~~~ggr##~~~,,,PP,gggggTgggggg,,##gggrgggggggg~~~ggrg,,#",
  "#.*.....F!..PP.../~~~~~#ggPPgggggPPgggggrr,#..c.*PP*.b..T~~~~~~gggggPPggggggggggrr,,P~~~rrr##~~~,,,PP,ggggggggggggT,##gggggggrrgg~~~~~ggg,,#",
  "#...........PP##########ggPPgggggPP,ggggrr,#.....PP.....T,~~~~~rggg,PPggggrggggrrr,,P~~~rrr##,~,,,,PPTgggggggggggg*,##gggggggggggg~~~gggg,,#",
  "############PP##########,,PP,,,,,PP,,gggg,,#,,,,,PP,,,,,,,,,,,,,,,,,PP,ggg,,,,,,,,,,PP,,,,,##TTTTTTPPTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTT#",
  "#,,,,,,,,,,,PP,,,,,,,,,,*,PP,T,,,PP,,,,rrr,#TrrrTPPrTrrrTrrrTT/TT/TTPPT/TT/TrrrrrrrrPPrrrrr##T,T,,,PP,TT,,T,,,,,~~~,T,,TT,,T,,,,TTT,,,,,,,,#",
  "#,,TTTTTT,,*PP,gg,g,gggg,,PP,,,,,PP,,,,rrr,#,ggg,PP,,,ggg,,,T,,,,,,,PP,,,,,,r,,,,,,,PP,,,,,##,,,,,,PP,ggggggggg,~~~Tggggggggggggggggggggg,T#",
  "#,,TTTTTT,,!PP,gg,gggggg,,PP,,,,,PP,,,,,T,,#,ggg,PP,,ggggg,,,,~~~/,,PP,ggg,,,,~~~,,,PP,ggg,##,,,,,,PP,ggggggggg~~~~,ggggggggggggggggggggg,,#",
  "#,T,,,,,T,,,PP,ggT,gggg,,,PP,,,,,PP*,,,,,,,#,ggg,PP,,,rgg,,T,/~~~~,,PPggggg,r~~~~~,,PPggggg##T,,,,,PP,ggggggggg~~~~Tggggggggggggggggggggg,T#",
  "#,,,,PPPPPPPPPPPPPPPPPPPP!PPPPPPPPPPPPPgg,,#,,,,,PP!,,,,,,,,T,~~~,,!PP,ggg,,,,~~~,,!PP,ggg,##T,T,,,PP,ggggggggg~~~~,ggggggggggggggggggggg,,#",
  "#,,,gPPPPPPPKPPPPPPPPPPPPPKPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP##,,,,,,PP,!,,,,,,,,,~~~,,,,,,,,,,,,,,,,,,,,,,,,#",
  "#,,,,gggg,!,PP,,,,,,,,,,,*,,!,,,,PP!,,,,,,,PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP##T,,,,,PP,PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP,#",
  "#,,ggggggg*,PP,gg,,,ggg,g,,,,ggg,PP,gggg,,,#,T,,r,PPK,,,,,T,T,,,,,,,,,,,,,,Trg,,,,K,,,,,,,r##,TT,,,PP,PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPT#",
  "#,,ggg,,gg,,PP*ggg,,ggggg,,,,ggg,PP,ggggg,,#,,ggg,PP,T,ggg,,T,ggg,K,,,T,~~~,ggg,,ggr,,,~~~,##,,T,,,PP,,,,,,,,,,,~~~,,,,,,,K,,,,,,,,,,,,,,,,#",
  "#,T,ggggggg,PP,ggg,,ggggg,,,,ggg,PP,ggggg,,#,gggggPPggggggg,,ggggg,,,,,/~~~~rgg,ggggg,~~~~~##,,,,,,PP,ggggggggg,~~~~ggggggggggggggggggggg,T#",
  "#,,,,gggg,g,PP,gg,,/~~~,,,,,,ggg,PP,!,ggg,,#,gggggPPggggggg,Tggggg,,,,,~~~~~ggg,ggggg,~~~~~##,,,,,,PP,gggggggggT~~~~gggggggggggggggggggggT,#",
  "#,,T,,,,,,,,PPPPPPPPPPPPPPPPPPPPPPK*ggggg,,#,gggggPPggggggg,,gggggggg,,~~~~~rg,~ggggg,~~~~~##T,,,,,PP,ggggggggg,~~~~ggggggggggggggggggggg,,#",
  "#,,TTTTTTTT,PPPPPPPPPPPPPPPPPPPPPPP,/~~~rr,#,,ggg,PP,ggggg/,,,gggggggg,,/~~,,,,~~ggg,,,~~~,##,,,T,,PP,ggggggggg,~~~,gggggggggggggggggggggT,#",
  "#,,TTTTTTT,,,,,,T,rrrrrrrr,,,,,,,,,,/~~~rr,#T,,,,!PPT,,,/~~,Tgg,,,ggg,,,,,,,r,,~~~,,,,r,,,,##,,,,,,PP,,TT,T,,,,,~~~,,T,,,,,,,,,,,,,,,,,,,,,#",
  "#,,,,,,,,,,,,,,,,,rrrrrrrr,,,,,,,,,,,,,,,,,#,,,,,,PP,,,,,,,,,,,,,T,,,,,,,,,,,r,,,,,,,,,,,,,########PP#######################################",
  "##################################################PP###############################################PP#######################################",
  "##################################################PP########################################T#.....PP...#T,TTTTTTTTTTTTTTTTTTTTTTTTTTTTTTTT#",
  "#T,TrT,rTr,,Trrrr~~~r,,,rrTTT,r,TrTrT,rr,Trr.RRRR!PP.RRRRR..TTTTT,,T,TT,gggTT,TTTTTTTTT,TT,#.RRRRR.PP.RRRR.T,,gggggggggggggggTggggg~~~gg,,,#",
  "#,,,,,,ggggg,r,,~~~~~,,,,r,ggggggg,r,ggg,,,TlRRRR.PP.RRRRR.lT,,ggggg,,,,ggg,,~~~,,ggggg,,,,#.RRRRR.PP.RRRR.,,,grggrggrggrggrggrggr~~~~~rg,,#",
  "#,,T,,ggggggg,,T,~~~,,,T,,ggggggggg,ggggg,Tr.RRRR*PP*RRRRR..TTgggggggT,,ggg,~~~~~ggggggg,T,#.RRRRR.PP.RRRR.T,,ggggggggggggggggggggg~~~ggg,,#",
  "#,,,,,,ggggg,,,,,,,,,,,,,,,ggggggg,,,ggg,,!TlRDRR.PP.RRDRRclT!,ggggg,,,,,,,,,~~~,,ggggg,,!,#cRRDRR.PPlRDRR.l,!ggggggggggggggggggggggggggg,,#",
  "#,PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP,#",
  "#,PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP,#",
  "#,,,,,,,,,,,,,,,,,,,PPK,,,,,,,,,,,,,,,,,,,,rRRRRRlPPlRRRRRR.T,,,,,,,,,,,,,PPK,,,,,,,,,,,,,r#.*..*..PP..*...T,,ggggggg~~~ggKggggggggggggg,,,#",
  "#,T,ggggg,,T,ggg,,,,PP,,,~~~,T,ggggg,,,,,,rTRRRRR.PP.RRRRRR.T,T,,ggg,~~~T,PP,,,Tggggg,,ggg,#.RRRR..PP..RRRR.,,ggggggg~~~ggggggTgggggggggg,,#",
  "#,,ggggggg,,ggggg,,,PP,,/~~~~,ggggggg,,,T,,rRRDRR.PP.RRRDRR.r,,,ggggg~~~,,PP,,,ggggggg,ggg,#.RRRR..PP..RRRR.,,ggrggrg~~~grggrggrggrggrggr,,#",
  "#,,,rgggg,r,,ggg,T,,PPr,,~~~r,,gggggT,,,,,,Tb...*.PP.*.....cT,,,,gggr~~~,,PP,T,,ggggg,Tggg,#.RRDR..PP..RDRR.,,TgTggggTggggggTggggggggggTg,,#",
  "#,,ggg,~~~~~~~ggg,r,PP!,r,ggg,,~~~~~~~,,,r,,T,,,,,PP,!,,ggg,~~~~~,,ggg,,,,PP,,,,,ggggg,,,~~#......!PP.......TTTTTTTTTTTTTTTTTTTTTTTTTTTTTTT#",
  "#,,ggg~~~~~~~ggggg,,PP,,,ggggg~~~~~~~~~,,,,,r,,r,,PP,,,ggggg~~~~~~ggggg,,,PP,,r,ggggggg,,~~#T.*..l.PP.l..*.T,,,gggTgggg,gggggggg,ggggTggg,,#",
  "#,,ggg,~~~~~~~ggg,,,PP,,,,ggg,,~~~~~~~,,,,,,T,,,,,PP,T,,ggg,~~~~~,,ggg,,,,PP,,,,,ggggg,,,~~#T......PP.....,T,,,gggggggg,gggggggg,gggggggg,,#",
  "#,T,,,,T,,,,T,,,,,,,PP,K,,,,,,T,,,,,,,,,T,,,r,,,,,PP,,,,,,,,,,,,T,,,,,,T,,PPK,,,T,,,,,,,T,,#.*.....PP......,,!,gggggTgg,gggggggg,gggggggg,,#",
  "#,PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP,#",
  "#,PPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPPP,#",
  "#,,r,,,,,T,,,,,,,,,,,r,,,,,,,,ggg,T,,,,,,,,TTT,,,,,,,,,T,,,,,,r,,,,,,,,,,,,,ggg,,,,,,,,,,T,#T,.*..*..*..*.,T,,ggggbggggggTKgggggggbggggg,,,#",
  "#,,~~~~~~~,,ggggg,,T,,,,ggggg,ggg,,~,~~~~~,,r,~~~~~,,,,,ggggg,,,,T,,ggggg,,,ggg,,~~~~~~~,,,#TT,..........,TT,,gFFFFggggFFFFFggT~~~ggFFFFg,,#",
  "#,~~~~~~~~~ggggggg,,,,,gggggggggg,~~~~~~~~~,T~~~~~~~,,,ggggggg,,,,,ggggggg,,ggg,~~~~~~~~,,,#TTT,,,,,,,,,,TTT,,gggggggggggggggg~~~~~gggggg,,#",
  "#,,~~~~~~~,,gggggr,,,,T,ggggg,,,,,,,~~~~~~,,r,,~~~~,,,,,ggggg,,,,,,,ggggg,r,,,,T,~~~~~~~,,,#TTTTTTTTTTTTTTTTTTTT,,,,T,T,TTTT,T,TTTTTTTTTTTT#",
  "############################################################################################################################################",
];

export const MAP_W = Math.max(...RAW.map((row) => row.length));
export const MAP_H = RAW.length;

export const MAP: TileId[][] = RAW.map((row) =>
  [...row.padEnd(MAP_W, "#")].map((ch) => LEGEND[ch] ?? "void"),
);

export type SpotKind = "heal" | "shop" | "pc" | "sign" | "rest" | "market" | "raid" | "crowns" | "hall";

export interface Spot {
  x: number;
  y: number;
  kind: SpotKind;
  title: string;
  body: string;
  veteranBody?: string;
}

export const SPOTS: Spot[] = [
  { x: 4, y: 5, kind: "heal", title: "Cope Clinic", body: "A kettle and a cot. HP and PP come back." },
  { x: 13, y: 5, kind: "shop", title: "Signal Stall", body: "Tins, sips, and peel-on flex." },
  { x: 4, y: 9, kind: "pc", title: "Meme Box", body: "Overflow crew sleeps in the crate." },
  { x: 3, y: 6, kind: "sign", title: "Stubtown", body: "One clinic. One stall. One crate. B opens MAP and PLAN for the road." },
  { x: 12, y: 6, kind: "sign", title: "Drippond Route", body: "West pond: Honklet and Ehcorn. Wilds Lv3-5. South: Chartwood." },
  { x: 25, y: 8, kind: "sign", title: "Drippond Route", body: "Lv3-5 beds. North: Cloutling. South: Slayling. West: clinic." },
  { x: 32, y: 8, kind: "sign", title: "Cope Cliffs", body: "Wilds Lv8-13. Ilex: east, duel Lv14-16. Kettle: north. South: Yield Yard. Far east: Frontier Post." },
  { x: 11, y: 16, kind: "sign", title: "Chartwood", body: "Wilds Lv7-11. Pella: east, duel Lv9-10. Kettle: south. Vell: southwest." },
  { x: 25, y: 18, kind: "sign", title: "Gas Gulch", body: "Lv11-15. Fee goblins by warm stones. Kettle: southeast. East: Yield Yard." },
  { x: 35, y: 20, kind: "sign", title: "Yield Yard", body: "Lv14-22. Cold reeds and rare forms. Kettle: south. West: Gas Gulch. East: the Frontier." },
  { x: 23, y: 7, kind: "rest", title: "Reed Kettle", body: "A warm flask beside the pond." },
  { x: 33, y: 7, kind: "rest", title: "Cliff Kettle", body: "A windbreak and one stubborn flame." },
  { x: 12, y: 19, kind: "rest", title: "Canopy Kettle", body: "A pot of tea beneath the ticket leaves." },
  { x: 26, y: 19, kind: "rest", title: "Gulch Kettle", body: "Steam is the only fee here." },
  { x: 34, y: 25, kind: "rest", title: "Snowmelt Kettle", body: "Yieldyeti left the thermos full." },
  { x: 10, y: 20, kind: "sign", title: "Canopy Beds", body: "Wilds Lv7-11. Vell's trial: west, Lv19-22. Clear it for stronger southern wilds.",
    veteranBody: "South grass: Lv22-25 evolved forms. North grass: Lv7-11. Mica: south path, circuit 2/3. Kettle: east." },
  { x: 28, y: 20, kind: "sign", title: "Warmstone Beds", body: "Wilds Lv11-15. Clear Vell's trial for stronger southern wilds. Kettle: northwest.",
    veteranBody: "South grass: Lv24-27. North grass: Lv11-15. Nix: south path, circuit 3/3. Kettle: northwest." },
  { x: 36, y: 24, kind: "sign", title: "Cold Reed Beds", body: "Wilds Lv14-22. Clear Vell's trial for stronger southern wilds. Rook and kettle: southwest.",
    veteranBody: "South grass: Lv26-29. North grass: Lv14-22. Rook: southwest, circuit 1/3. Rest before the first stamp." },
  // ---- Frontier ------------------------------------------------------------------------------
  { x: 47, y: 4, kind: "heal", title: "Frontier Clinic", body: "The last cot before the wilds. HP and PP come back." },
  { x: 52, y: 4, kind: "shop", title: "Frontier Stall", body: "Tins and flasks, frontier prices." },
  { x: 46, y: 12, kind: "pc", title: "Frontier Meme Box", body: "The same crate network: your boxed crew is in here too." },
  { x: 54, y: 5, kind: "sign", title: "Frontier Post", body: "East: Frostfloor Lv20-24, then the Crystal Cache. South road: Candle Dunes, Rugmire, Magma Mint." },
  { x: 57, y: 5, kind: "sign", title: "Frostfloor", body: "HODL country, Lv20-24; grown forms Lv31-34 in the south drift. Coldkey Frida waits by the kettle." },
  { x: 75, y: 5, kind: "sign", title: "Crystal Cache", body: "Lv24-28; grown forms Lv32-35 in the south vault, where a wyrm from the first block sleeps. Quartz guards the lane." },
  { x: 51, y: 18, kind: "sign", title: "Candle Dunes", body: "BURN and FLEX, Lv18-24; grown forms Lv30-34 in the south flats. Dunebroker Kade duels by the kettle." },
  { x: 67, y: 18, kind: "sign", title: "Rugmire", body: "RUG types, Lv22-26; grown forms Lv31-35 in the south sink, and something huge naps there. Morrow wades west." },
  { x: 83, y: 18, kind: "sign", title: "Magma Mint", body: "Lv26-30; grown forms Lv32-38 on the south flow. Burnmaster Cinder rules the vents. Bring LIQUID and SIGNAL." },
  { x: 66, y: 8, kind: "rest", title: "Frost Kettle", body: "Somebody wrapped it in three scarves." },
  { x: 82, y: 8, kind: "rest", title: "Cache Kettle", body: "It hums in the same key as the crystals." },
  { x: 52, y: 21, kind: "rest", title: "Dune Kettle", body: "Hot tea in the desert. Somehow it helps." },
  { x: 66, y: 22, kind: "rest", title: "Bog Kettle", body: "Raised on stilts. The water is not for drinking." },
  { x: 82, y: 21, kind: "rest", title: "Vent Kettle", body: "Boils on its own. Free, like the steam." },
  // ---- Undernet ------------------------------------------------------------------------------
  { x: 49, y: 27, kind: "sign", title: "Undernet Gate", body: "South: Neon Harbor and the Undernet. Wilds Lv30-40. Rest at the Dune Kettle first." },
  { x: 46, y: 34, kind: "heal", title: "Harbor Clinic", body: "Neon cots, same old kettle. HP and PP come back." },
  { x: 55, y: 34, kind: "shop", title: "Harbor Stall", body: "Tins and flasks under a buzzing sign." },
  { x: 58, y: 35, kind: "pc", title: "Harbor Meme Box", body: "A crate on the quay, wired to every other Meme Box." },
  { x: 46, y: 39, kind: "market", title: "Meme Market", body: "List a creature, browse every scout's listings, buy with coins." },
  { x: 56, y: 39, kind: "raid", title: "Raid Gate", body: "A world boss stirs past the gate. Every scout's hits count together." },
  { x: 49, y: 31, kind: "sign", title: "Neon Harbor", body: "The Undernet. West: Mempool Caverns. East: Neon Grid. South: Coral Ledger and Cloud Chain." },
  { x: 42, y: 34, kind: "sign", title: "Mempool Caverns", body: "Lv30-34; grown forms Lv35-38 in the south grotto. Spelunker Opal waits west of the kettle." },
  { x: 61, y: 34, kind: "sign", title: "Neon Grid", body: "AI country, Lv33-35; grown forms Lv37-40 in the south subnet, where a mind of pure weights hides. Vex runs the east end." },
  { x: 22, y: 41, kind: "sign", title: "Coral Ledger", body: "Lv31-34; grown forms Lv36-39 on the south reef. Reefbroker Nami trades east of the kettle." },
  { x: 53, y: 41, kind: "sign", title: "Cloud Chain", body: "MOON types, Lv35-38; grown forms Lv38-41 in the south nimbus. Skykeeper Lune guards the far east." },
  { x: 22, y: 37, kind: "rest", title: "Shroom Kettle", body: "Warmed by glowing caps. Tastes faintly of forest." },
  { x: 76, y: 37, kind: "rest", title: "Grid Kettle", body: "Liquid-cooled. Somehow still hot." },
  { x: 23, y: 44, kind: "rest", title: "Reef Kettle", body: "Salt on the rim. The tide keeps it topped up." },
  { x: 76, y: 44, kind: "rest", title: "Sky Kettle", body: "It floats a finger above the cloud. Nobody asks." },
  // ---- Sidechain -----------------------------------------------------------------------------
  { x: 89, y: 34, kind: "sign", title: "Sidechain Bridge", body: "East: Bridgeport and the Sidechain. Wilds Lv38-50. Bring a crew that has beaten Vex." },
  { x: 95, y: 34, kind: "heal", title: "Bridgeport Clinic", body: "Cots stacked two chains high. HP and PP come back." },
  { x: 103, y: 34, kind: "shop", title: "Bridgeport Stall", body: "Everything the Sidechain sells, in both currencies." },
  { x: 92, y: 35, kind: "pc", title: "Bridgeport Meme Box", body: "The last crate on the chain. Your boxed crew waits inside." },
  { x: 95, y: 40, kind: "hall", title: "Hall of Crowns", body: "Enshrined and unsold Crowns rest here, with every season's Felled-by names." },
  { x: 104, y: 40, kind: "crowns", title: "Crown House", body: "Crown auctions, the Crown market, your vault and Brass cosmetics." },
  { x: 98, y: 41, kind: "sign", title: "Bridgeport", body: "The Sidechain. East: Ghostchain (Lv40-43) and Halving Harvest (Lv38-41). North: Pump Jungle." },
  { x: 109, y: 44, kind: "sign", title: "Halving Harvest", body: "STAKE and REKT, Lv38-41. Harvester Juno works the far field. Start here." },
  { x: 109, y: 34, kind: "sign", title: "Ghostchain", body: "REKT country, Lv40-43. Dead coins do not rest. Gravewarden Ash keeps the east gate." },
  { x: 102, y: 19, kind: "sign", title: "Pump Jungle", body: "Lv42-45. Everything here only goes up, including the trees. Ranger Kiri waits east." },
  { x: 101, y: 5, kind: "sign", title: "Sakura Stake", body: "Lv44-47. A blossom stag is seen here once per halving. Gardener Hana tends the east pond." },
  { x: 118, y: 5, kind: "sign", title: "Rugpull Ruins", body: "Lv46-49. The first chain ever rugged lies here. Curator Osric guards the last vault." },
  { x: 122, y: 47, kind: "rest", title: "Harvest Kettle", body: "Spiced tea in a hollow pumpkin. Still warm." },
  { x: 122, y: 37, kind: "rest", title: "Grave Kettle", body: "Somebody keeps it lit. Nobody has seen who." },
  { x: 122, y: 22, kind: "rest", title: "Jungle Kettle", body: "Hung from a vine over a tiny fire. Smells of rain." },
  { x: 108, y: 8, kind: "rest", title: "Blossom Kettle", body: "Petals fall into the cup. They are part of the recipe." },
  { x: 126, y: 8, kind: "rest", title: "Ruin Kettle", body: "An ancient brazier. The fire inside is new." },
];

export const SPAWN = { x: 4, y: 11 };
export const WAKE = { x: 4, y: 6 };
export const GRASS_PITY = 10;
export const GRASS_QUIET = 2;

/** 12% base, +0.8% per step, guaranteed on the pity step. */
export function grassEncounterChance(streak: number): number {
  if (streak <= 0) return 0;
  if (streak >= GRASS_PITY) return 1;
  return 0.12 + (streak / GRASS_PITY) * 0.08;
}

export interface EncounterRow {
  id: SpeciesId;
  w: number;
  lo: number;
  hi: number;
}

// Drippond's whole catalog is fourteen faces at Lv3-5. Regulars that headline a later
// route (Fomoling, Slippeel, Bagsnail, Apeling, Rugfox, Moonling, Gigachomp, Chopperdog,
// Whalord, Finekit) are met there instead of diluting the first patch of grass.
const DRIPPOND_TABLE: EncounterRow[] = [
  // Route-wide pair: one sturdy Cope body, one fast Signal biter, so any bed can start a crew.
  { id: "wojakle", w: 18, lo: 3, hi: 5 },
  { id: "laserpup", w: 14, lo: 4, hi: 5 },
  // West: the pond and the town path out of Stubtown.
  { id: "bogfiend", w: 20, lo: 3, hi: 5 },
  { id: "honklet", w: 16, lo: 3, hi: 5 },
  { id: "ehcorn", w: 14, lo: 3, hi: 5 },
  { id: "shibonk", w: 12, lo: 4, hi: 5 },
  // Northeast: the loud shore under Tavi's kettle bell.
  { id: "cloutling", w: 18, lo: 3, hi: 5 },
  { id: "capling", w: 16, lo: 3, hi: 5 },
  { id: "ratiofin", w: 14, lo: 3, hi: 5 },
  { id: "vibeling", w: 12, lo: 4, hi: 5 },
  // Southeast: the ticket grass on the way down to Chartwood.
  { id: "slayling", w: 16, lo: 3, hi: 5 },
  { id: "sauceling", w: 14, lo: 3, hi: 5 },
  { id: "toquelin", w: 14, lo: 3, hi: 5 },
  { id: "queueling", w: 12, lo: 3, hi: 5 },
];

// Three physical beds of six rows: the two route-wide regulars plus four locals, so a
// patch teaches a readable handful of faces instead of the whole Index at once.
// West reads Liquid/Cope, northeast Viral/Signal, southeast Flex/Cope.
const DRIPPOND_COMMON: SpeciesId[] = ["wojakle", "laserpup"];
const dripBed = (ids: SpeciesId[]): EncounterRow[] =>
  DRIPPOND_TABLE.filter(row => DRIPPOND_COMMON.includes(row.id) || ids.includes(row.id));
export const DRIPPOND_BEDS = {
  west: dripBed(["bogfiend", "honklet", "ehcorn", "shibonk"]),
  northeast: dripBed(["cloutling", "capling", "ratiofin", "vibeling"]),
  southeast: dripBed(["slayling", "sauceling", "toquelin", "queueling"]),
};

export type ZoneId = "stubtown" | "drippond" | "copeCliffs" | "chartwood" | "gasGulch" | "yieldYard"
  | "frontierPost" | "frostfloor" | "crystalCache" | "candleDunes" | "rugmire" | "magmaMint"
  | "neonHarbor" | "mempoolCaverns" | "neonGrid" | "coralLedger" | "cloudChain"
  | "bridgeport" | "halvingHarvest" | "ghostchain" | "pumpJungle" | "sakuraStake" | "rugpullRuins";

export const ZONES: Record<ZoneId, { name: string; encounters: EncounterRow[] }> = {
  stubtown: { name: "Stubtown Plaza", encounters: [] },
  drippond: { name: "Drippond Route", encounters: DRIPPOND_TABLE },
  copeCliffs: { name: "Cope Cliffs", encounters: [
    { id: "fomoling", w: 22, lo: 8, hi: 11 },
    { id: "hopiumite", w: 30, lo: 8, hi: 11 },
    { id: "wojakle", w: 35, lo: 8, hi: 11 },
    { id: "moonling", w: 30, lo: 9, hi: 12 },
    { id: "gigachomp", w: 25, lo: 10, hi: 13 },
    { id: "rugfox", w: 10, lo: 9, hi: 12 },
    { id: "lurkit", w: 22, lo: 8, hi: 11 },
    { id: "npcat", w: 18, lo: 8, hi: 11 },
    { id: "seethtoad", w: 20, lo: 8, hi: 11 },
    { id: "deluling", w: 18, lo: 8, hi: 11 },
    { id: "draftkit", w: 18, lo: 8, hi: 11 },
    { id: "ghostkit", w: 18, lo: 8, hi: 11 },
    { id: "midling", w: 18, lo: 8, hi: 11 },
    { id: "snoozlet", w: 18, lo: 8, hi: 11 },
    { id: "chillip", w: 18, lo: 8, hi: 11 },
  ] },
  chartwood: { name: "Chartwood", encounters: [
    { id: "mintmoth", w: 30, lo: 7, hi: 10 },
    { id: "chartgob", w: 35, lo: 8, hi: 11 },
    { id: "cloutling", w: 20, lo: 7, hi: 10 },
    { id: "hopiumite", w: 15, lo: 8, hi: 11 },
    { id: "apeling", w: 45, lo: 7, hi: 10 },
    { id: "rugfox", w: 30, lo: 8, hi: 11 },
    { id: "laserpup", w: 25, lo: 7, hi: 10 },
    { id: "yapling", w: 28, lo: 7, hi: 10 },
    { id: "gatekit", w: 22, lo: 7, hi: 10 },
    { id: "grassnudge", w: 22, lo: 7, hi: 10 },
    { id: "spoilpup", w: 18, lo: 7, hi: 10 },
    { id: "clipfinch", w: 16, lo: 8, hi: 11 },
    { id: "poutkit", w: 18, lo: 7, hi: 10 },
    { id: "crashpup", w: 16, lo: 8, hi: 11 },
    { id: "cookling", w: 18, lo: 7, hi: 10 },
    { id: "depkit", w: 16, lo: 8, hi: 11 },
    { id: "coneling", w: 16, lo: 8, hi: 11 },
    { id: "syrupup", w: 18, lo: 7, hi: 10 },
    { id: "crumbit", w: 18, lo: 7, hi: 10 },
    { id: "puckup", w: 16, lo: 8, hi: 11 },
  ] },
  gasGulch: { name: "Gas Gulch", encounters: [
    { id: "gasgob", w: 40, lo: 11, hi: 14 },
    { id: "slippeel", w: 15, lo: 11, hi: 14 },
    { id: "chartgob", w: 35, lo: 12, hi: 15 },
    { id: "hopiumite", w: 15, lo: 11, hi: 14 },
    { id: "rugfox", w: 40, lo: 11, hi: 14 },
    { id: "chopperdog", w: 35, lo: 12, hi: 15 },
    { id: "laserpup", w: 25, lo: 11, hi: 14 },
    { id: "scrollmite", w: 22, lo: 11, hi: 14 },
    { id: "finekit", w: 12, lo: 12, hi: 15 },
    { id: "glazeling", w: 16, lo: 12, hi: 15 },
    { id: "receiptling", w: 16, lo: 12, hi: 15 },
    { id: "pingling", w: 16, lo: 12, hi: 15 },
    { id: "lockkit", w: 16, lo: 12, hi: 15 },
    { id: "bufferret", w: 16, lo: 12, hi: 15 },
    { id: "tabbit", w: 16, lo: 12, hi: 15 },
  ] },
  yieldYard: { name: "Yield Yard", encounters: [
    { id: "yieldyeti", w: 28, lo: 15, hi: 18 },
    { id: "slippeel", w: 18, lo: 14, hi: 17 },
    { id: "fomobeast", w: 8, lo: 19, hi: 21 },
    { id: "airdropqueen", w: 8, lo: 18, hi: 20 },
    { id: "bagsnail", w: 20, lo: 14, hi: 16 },
    { id: "diamondshell", w: 8, lo: 18, hi: 20 },
    { id: "copiumax", w: 8, lo: 18, hi: 20 },
    { id: "cloutking", w: 8, lo: 18, hi: 20 },
    { id: "candlewyrm", w: 5, lo: 20, hi: 22 },
    { id: "whalord", w: 35, lo: 14, hi: 17 },
    { id: "chopperdog", w: 30, lo: 14, hi: 17 },
    { id: "moonling", w: 25, lo: 15, hi: 18 },
    { id: "bogbaron", w: 10, lo: 18, hi: 20 },
    { id: "ratioking", w: 8, lo: 18, hi: 20 },
    { id: "yapstorm", w: 8, lo: 18, hi: 20 },
    { id: "threadmaw", w: 8, lo: 19, hi: 21 },
    { id: "doomwyrm", w: 6, lo: 20, hi: 22 },
    { id: "kilnmutt", w: 8, lo: 18, hi: 20 },
    { id: "lorelord", w: 8, lo: 18, hi: 20 },
    { id: "gatewyrm", w: 7, lo: 18, hi: 20 },
    { id: "glazewyrm", w: 7, lo: 19, hi: 21 },
    { id: "nocapra", w: 7, lo: 18, hi: 20 },
    { id: "lawnlord", w: 7, lo: 18, hi: 20 },
    { id: "maldmaw", w: 7, lo: 19, hi: 21 },
    { id: "delulord", w: 7, lo: 19, hi: 21 },
    { id: "vibewyrm", w: 6, lo: 18, hi: 20 },
    { id: "postwyrm", w: 6, lo: 18, hi: 20 },
    { id: "leakmaw", w: 6, lo: 19, hi: 21 },
    { id: "linebaron", w: 6, lo: 18, hi: 20 },
    { id: "auditlord", w: 6, lo: 19, hi: 21 },
    { id: "cliplord", w: 6, lo: 19, hi: 21 },
    { id: "tuquelord", w: 6, lo: 18, hi: 20 },
    { id: "gravylord", w: 6, lo: 18, hi: 20 },
    { id: "ghostwyrm", w: 6, lo: 19, hi: 21 },
    { id: "crashmaw", w: 6, lo: 19, hi: 21 },
    { id: "saucelord", w: 6, lo: 18, hi: 20 },
    { id: "pinglord", w: 6, lo: 19, hi: 21 },
    { id: "slaylord", w: 6, lo: 18, hi: 20 },
    { id: "cookwyrm", w: 6, lo: 18, hi: 20 },
    { id: "midbaron", w: 6, lo: 18, hi: 20 },
    { id: "locklord", w: 6, lo: 19, hi: 21 },
    { id: "depbaron", w: 6, lo: 18, hi: 20 },
    { id: "ehvergreen", w: 5, lo: 18, hi: 20 },
    { id: "napocalypse", w: 5, lo: 18, hi: 20 },
    { id: "detourant", w: 5, lo: 19, hi: 21 },
    { id: "sapreme", w: 5, lo: 18, hi: 20 },
    { id: "lagomorph", w: 5, lo: 19, hi: 21 },
    { id: "honkference", w: 5, lo: 18, hi: 20 },
    { id: "chillossus", w: 5, lo: 18, hi: 20 },
    { id: "breadwinner", w: 5, lo: 18, hi: 20 },
    { id: "clapback", w: 5, lo: 19, hi: 21 },
    { id: "tabalanche", w: 5, lo: 19, hi: 21 },
  ] },
  // ---- Frontier ----------------------------------------------------------------------------
  frontierPost: { name: "Frontier Post", encounters: [] },
  candleDunes: { name: "Candle Dunes", encounters: [
    { id: "candlecub", w: 30, lo: 18, hi: 21 },
    { id: "shillpion", w: 26, lo: 18, hi: 21 },
    { id: "sprikle", w: 28, lo: 18, hi: 21 },
    { id: "gasbull", w: 16, lo: 19, hi: 22 },
    { id: "humpfund", w: 6, lo: 22, hi: 24 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "wickwolf", w: 24, lo: 30, hi: 33 },
    { id: "saguarocket", w: 22, lo: 30, hi: 33 },
    { id: "shillscorp", w: 18, lo: 31, hi: 34 },
    { id: "magmabull", w: 12, lo: 32, hi: 34 },
  ] },
  frostfloor: { name: "Frostfloor", encounters: [
    { id: "frostbag", w: 28, lo: 20, hi: 23 },
    { id: "pengwallet", w: 24, lo: 20, hi: 23 },
    { id: "icepup", w: 26, lo: 21, hi: 24 },
    { id: "yieldyeti", w: 10, lo: 22, hi: 24 },
    { id: "cachemole", w: 10, lo: 21, hi: 24 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "glacierbag", w: 24, lo: 31, hi: 34 },
    { id: "diamondpaws", w: 22, lo: 31, hi: 34 },
    { id: "coldvault", w: 20, lo: 32, hi: 34 },
    { id: "vaultmole", w: 14, lo: 31, hi: 34 },
  ] },
  rugmire: { name: "Rugmire", encounters: [
    { id: "ruglet", w: 26, lo: 22, hi: 25 },
    { id: "mirebot", w: 24, lo: 22, hi: 25 },
    { id: "botfly", w: 26, lo: 22, hi: 25 },
    { id: "honeytoad", w: 22, lo: 23, hi: 26 },
    { id: "bogbaron", w: 8, lo: 24, hi: 26 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "swarmlord", w: 22, lo: 31, hi: 34 },
    { id: "mireshill", w: 20, lo: 31, hi: 34 },
    { id: "rugwraith", w: 22, lo: 32, hi: 35 },
    { id: "honeybaron", w: 16, lo: 32, hi: 35 },
    { id: "ursarug", w: 1, lo: 38, hi: 40 },
  ] },
  crystalCache: { name: "Crystal Cache", encounters: [
    { id: "shardling", w: 28, lo: 24, hi: 27 },
    { id: "prismpup", w: 26, lo: 24, hi: 27 },
    { id: "nodelet", w: 24, lo: 25, hi: 28 },
    { id: "cachemole", w: 14, lo: 24, hi: 27 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "prismhound", w: 22, lo: 32, hi: 35 },
    { id: "validrake", w: 22, lo: 32, hi: 35 },
    { id: "shardking", w: 20, lo: 33, hi: 35 },
    { id: "vaultmole", w: 12, lo: 32, hi: 35 },
    { id: "genesiwyrm", w: 1, lo: 38, hi: 40 },
  ] },
  magmaMint: { name: "Magma Mint", encounters: [
    { id: "emberbit", w: 28, lo: 26, hi: 29 },
    { id: "gasbull", w: 20, lo: 26, hi: 29 },
    { id: "blazebit", w: 10, lo: 27, hi: 30 },
    { id: "shillpion", w: 12, lo: 26, hi: 29 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "magmabull", w: 22, lo: 33, hi: 36 },
    { id: "moltwisp", w: 20, lo: 32, hi: 35 },
    { id: "shillscorp", w: 20, lo: 33, hi: 36 },
    { id: "infernomint", w: 4, lo: 36, hi: 38 },
  ] },
  // ---- Undernet ------------------------------------------------------------------------------
  neonHarbor: { name: "Neon Harbor", encounters: [] },
  mempoolCaverns: { name: "Mempool Caverns", encounters: [
    { id: "pendbat", w: 28, lo: 30, hi: 33 },
    { id: "shroomint", w: 26, lo: 30, hi: 33 },
    { id: "hashgrub", w: 24, lo: 30, hi: 33 },
    { id: "geodeggo", w: 14, lo: 31, hi: 33 },
    { id: "cachemole", w: 8, lo: 30, hi: 32 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "stuckwing", w: 22, lo: 35, hi: 38 },
    { id: "shroomancer", w: 20, lo: 35, hi: 38 },
    { id: "asicarab", w: 20, lo: 35, hi: 38 },
    { id: "geodrake", w: 14, lo: 36, hi: 38 },
  ] },
  coralLedger: { name: "Coral Ledger", encounters: [
    { id: "shrimpling", w: 28, lo: 31, hi: 34 },
    { id: "sidecrab", w: 26, lo: 31, hi: 34 },
    { id: "jellyfud", w: 24, lo: 31, hi: 34 },
    { id: "slippeel", w: 8, lo: 31, hi: 33 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "krillionaire", w: 22, lo: 36, hi: 39 },
    { id: "crabstack", w: 20, lo: 36, hi: 39 },
    { id: "medusell", w: 20, lo: 36, hi: 39 },
    { id: "pearlclam", w: 12, lo: 36, hi: 38 },
  ] },
  neonGrid: { name: "Neon Grid", encounters: [
    { id: "chatbit", w: 28, lo: 33, hi: 35 },
    { id: "glitchpup", w: 24, lo: 33, hi: 35 },
    { id: "fanbit", w: 22, lo: 33, hi: 35 },
    { id: "ledmoth", w: 22, lo: 33, hi: 35 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "chatlord", w: 22, lo: 37, hi: 40 },
    { id: "glitchhound", w: 20, lo: 37, hi: 40 },
    { id: "fanstorm", w: 20, lo: 37, hi: 40 },
    { id: "rackmoth", w: 20, lo: 37, hi: 40 },
    { id: "neuralith", w: 1, lo: 39, hi: 40 },
  ] },
  cloudChain: { name: "Cloud Chain", encounters: [
    { id: "lunapup", w: 28, lo: 35, hi: 37 },
    { id: "nimbaa", w: 26, lo: 35, hi: 37 },
    { id: "rocketot", w: 24, lo: 35, hi: 37 },
    { id: "craterling", w: 14, lo: 36, hi: 38 },
    // Grown forms: the south patch trains a crew for this region's duels.
    { id: "lunawolf", w: 22, lo: 38, hi: 41 },
    { id: "nimbram", w: 20, lo: 38, hi: 41 },
    { id: "rocketitan", w: 18, lo: 38, hi: 41 },
    { id: "selenarch", w: 1, lo: 39, hi: 40 },
  ] },
  // ---- Sidechain ---------------------------------------------------------------------------
  bridgeport: { name: "Bridgeport", encounters: [] },
  halvingHarvest: { name: "Halving Harvest", encounters: [
    { id: "pumpkoin", w: 28, lo: 38, hi: 41 },
    { id: "crowlet", w: 26, lo: 38, hi: 41 },
    { id: "nutling", w: 24, lo: 38, hi: 41 },
    { id: "gourdian", w: 4, lo: 42, hi: 43 },
    { id: "crowllateral", w: 3, lo: 42, hi: 43 },
    { id: "nutvault", w: 3, lo: 42, hi: 43 },
  ] },
  ghostchain: { name: "Ghostchain", encounters: [
    { id: "rektling", w: 28, lo: 40, hi: 43 },
    { id: "tombstonk", w: 24, lo: 40, hi: 43 },
    { id: "webthree", w: 24, lo: 40, hi: 43 },
    { id: "deadcat", w: 22, lo: 40, hi: 43 },
    { id: "rektoplasm", w: 4, lo: 44, hi: 45 },
    { id: "cryptkeep", w: 3, lo: 44, hi: 45 },
    { id: "arachnft", w: 3, lo: 44, hi: 45 },
    { id: "catacomb", w: 3, lo: 44, hi: 45 },
  ] },
  pumpJungle: { name: "Pump Jungle", encounters: [
    { id: "toucoin", w: 28, lo: 42, hi: 45 },
    { id: "vinesnek", w: 26, lo: 42, hi: 45 },
    { id: "slothlet", w: 22, lo: 42, hi: 45 },
    { id: "apemajor", w: 8, lo: 42, hi: 44 },
    { id: "beakfolio", w: 4, lo: 46, hi: 47 },
    { id: "vineconda", w: 3, lo: 46, hi: 47 },
    { id: "slothstake", w: 3, lo: 46, hi: 47 },
  ] },
  sakuraStake: { name: "Sakura Stake", encounters: [
    { id: "koinfish", w: 28, lo: 44, hi: 47 },
    { id: "origamint", w: 26, lo: 44, hi: 47 },
    { id: "sproutle", w: 24, lo: 44, hi: 47 },
    { id: "ryukoin", w: 4, lo: 47, hi: 48 },
    { id: "craneprint", w: 3, lo: 47, hi: 48 },
    { id: "bonsaitoise", w: 3, lo: 47, hi: 48 },
    { id: "arborealm", w: 1, lo: 49, hi: 50 },
  ] },
  rugpullRuins: { name: "Rugpull Ruins", encounters: [
    { id: "golemint", w: 28, lo: 46, hi: 49 },
    { id: "riddlekit", w: 26, lo: 46, hi: 49 },
    { id: "wraplet", w: 26, lo: 46, hi: 49 },
    { id: "tombstonk", w: 8, lo: 46, hi: 48 },
    { id: "colosstake", w: 4, lo: 48, hi: 49 },
    { id: "sphynxfi", w: 3, lo: 48, hi: 49 },
    { id: "wrappharaoh", w: 3, lo: 48, hi: 49 },
    { id: "rektquiem", w: 1, lo: 49, hi: 50 },
  ] },
};

/** First frontier column: everything east of the original 44-wide map. */
export const FRONTIER_X = 44;
/** First Undernet row: everything below the original 30-row map. */
export const UNDERNET_Y = 30;
/** First Sidechain column: everything east of the 92-wide Frontier / Undernet map. */
export const SIDECHAIN_X = 92;

export function zoneAt(x: number, y: number): ZoneId {
  if (x >= SIDECHAIN_X) {
    const lx = x - SIDECHAIN_X;
    if (y >= UNDERNET_Y) return lx < 16 ? "bridgeport" : y <= 40 ? "ghostchain" : "halvingHarvest";
    if (y >= 14) return "pumpJungle";
    return lx < 24 ? "sakuraStake" : "rugpullRuins";
  }
  if (y >= UNDERNET_Y) {
    if (y <= UNDERNET_Y + 10) return x < 44 ? "mempoolCaverns" : x < 60 ? "neonHarbor" : "neonGrid";
    return x < 44 ? "coralLedger" : "cloudChain";
  }
  if (x >= FRONTIER_X) {
    const lx = x - FRONTIER_X;
    if (y <= 13) return lx < 12 ? "frontierPost" : lx < 30 ? "frostfloor" : "crystalCache";
    return lx < 16 ? "candleDunes" : lx < 32 ? "rugmire" : "magmaMint";
  }
  if (x >= 30) return y < 16 ? "copeCliffs" : "yieldYard";
  if (y >= 14) return x < 17 ? "chartwood" : "gasGulch";
  return x < 16 ? "stubtown" : "drippond";
}

/**
 * Post-trial southern beds diversify the climb to 30; northern habitats stay gentle.
 * Every species a circuit trainer fields sits at w >= 16 in its bed, so the deck the
 * circuit plays is a deck the player can go and recruit rather than only read about.
 */
export const VETERAN_ENCOUNTERS: Partial<Record<ZoneId, EncounterRow[]>> = {
  chartwood: [
    { id: "cloutking", w: 25, lo: 22, hi: 24 },
    { id: "airdropqueen", w: 25, lo: 22, hi: 24 },
    { id: "apemajor", w: 20, lo: 23, hi: 25 },
    { id: "laserhound", w: 20, lo: 23, hi: 25 },
    { id: "candlewyrm", w: 10, lo: 24, hi: 25 },
    { id: "yapstorm", w: 18, lo: 22, hi: 24 },
    { id: "gatewyrm", w: 16, lo: 22, hi: 24 },
    { id: "lawnlord", w: 16, lo: 23, hi: 25 },
    { id: "postwyrm", w: 16, lo: 22, hi: 24 },
    { id: "leakmaw", w: 14, lo: 23, hi: 25 },
    { id: "gravylord", w: 16, lo: 22, hi: 24 },
    { id: "crashmaw", w: 16, lo: 23, hi: 25 },
    { id: "cookwyrm", w: 16, lo: 22, hi: 24 },
    { id: "depbaron", w: 14, lo: 23, hi: 25 },
    { id: "sapreme", w: 10, lo: 22, hi: 24 },
    { id: "breadwinner", w: 10, lo: 22, hi: 24 },
  ],
  gasGulch: [
    { id: "gasgob", w: 25, lo: 24, hi: 26 },
    { id: "rugwolf", w: 25, lo: 24, hi: 26 },
    { id: "copiumax", w: 20, lo: 24, hi: 27 },
    { id: "chopperalpha", w: 20, lo: 25, hi: 27 },
    { id: "candlewyrm", w: 10, lo: 25, hi: 27 },
    { id: "doomwyrm", w: 18, lo: 24, hi: 27 },
    { id: "kilnmutt", w: 16, lo: 24, hi: 26 },
    { id: "glazewyrm", w: 14, lo: 24, hi: 26 },
    { id: "maldmaw", w: 16, lo: 25, hi: 27 },
    { id: "auditlord", w: 16, lo: 24, hi: 26 },
    { id: "cliplord", w: 16, lo: 25, hi: 27 },
    { id: "pinglord", w: 16, lo: 25, hi: 27 },
    { id: "locklord", w: 16, lo: 24, hi: 26 },
    { id: "detourant", w: 10, lo: 24, hi: 26 },
    { id: "lagomorph", w: 10, lo: 25, hi: 27 },
    { id: "clapback", w: 16, lo: 24, hi: 26 },
    { id: "tabalanche", w: 10, lo: 25, hi: 27 },
  ],
  yieldYard: [
    { id: "yieldyeti", w: 25, lo: 26, hi: 28 },
    { id: "diamondshell", w: 20, lo: 26, hi: 28 },
    { id: "fomobeast", w: 20, lo: 26, hi: 29 },
    { id: "moonlord", w: 20, lo: 27, hi: 29 },
    { id: "bogbaron", w: 15, lo: 27, hi: 29 },
    { id: "threadmaw", w: 16, lo: 26, hi: 28 },
    { id: "ratioking", w: 16, lo: 26, hi: 28 },
    { id: "lorelord", w: 16, lo: 27, hi: 29 },
    { id: "nocapra", w: 12, lo: 26, hi: 28 },
    { id: "delulord", w: 16, lo: 27, hi: 29 },
    { id: "vibewyrm", w: 16, lo: 26, hi: 28 },
    { id: "linebaron", w: 16, lo: 26, hi: 28 },
    { id: "tuquelord", w: 16, lo: 26, hi: 28 },
    { id: "ghostwyrm", w: 16, lo: 27, hi: 29 },
    { id: "saucelord", w: 12, lo: 26, hi: 28 },
    { id: "slaylord", w: 12, lo: 26, hi: 28 },
    { id: "midbaron", w: 16, lo: 26, hi: 28 },
    { id: "ehvergreen", w: 16, lo: 26, hi: 28 },
    { id: "napocalypse", w: 16, lo: 26, hi: 28 },
    { id: "honkference", w: 16, lo: 26, hi: 28 },
    { id: "chillossus", w: 16, lo: 26, hi: 28 },
  ],
};

/**
 * Outside Drippond every zone rolled one table for every blade of grass, so a single patch
 * offered the whole catalog: Yield Yard's pre-trial bed was fifty-two faces on thirty-two
 * tiles. Each zone now names the patches the map actually draws and hands each one a
 * readable few, the way Drippond's three beds already do.
 *
 * The zone tables above are untouched. They stay the catalog the Field Guide and the route
 * plan read, and every row in them is still met somewhere in its own zone -- the beds below
 * partition their zone's table, they do not trim it.
 */
export type PatchId =
  | "scree" | "face" | "ledge"
  | "ticket" | "grove" | "hollowWest" | "hollowEast" | "southStrip"
  | "warmstones" | "westEdge" | "southBeds" | "eastEdge"
  | "shelf" | "ridge" | "reedsUpper" | "reedsDeep" | "melt"
  | "floe" | "drift" | "geode" | "vault" | "crest" | "flats" | "hummock" | "sink" | "vents" | "flow"
  | "vein" | "grotto" | "rack" | "subnet" | "shallows" | "reef" | "cirrus" | "nimbus"
  | "orchard" | "fields" | "graves" | "crypts" | "canopy" | "understory" | "petals" | "koi" | "colonnade" | "vault";

/** Which drawn patch a tile belongs to. Total over each zone, so paths answer like the grass beside them. */
function patchAt(zone: ZoneId, x: number, y: number): PatchId | null {
  switch (zone) {
    // Three shelves: the western scree, the cliff face, and the high ledge above the road.
    case "copeCliffs": return x < 34 ? "scree" : y < 7 ? "ledge" : "face";
    // The chart strip east, Vell's grove, the hollow either side of it, the strip down to Mica.
    case "chartwood": return x >= 14 ? (y < 21 ? "ticket" : "southStrip")
      : y < 21 ? "grove" : x < 7 ? "hollowWest" : "hollowEast";
    // Warm stones north of the toll road; three separate south beds below it.
    case "gasGulch": return y < 21 ? "warmstones"
      : x < 19 ? "westEdge" : x >= 27 ? "eastEdge" : "southBeds";
    // The road-side reed shelf, the ridge, and the cold reeds in three bands down to the melt.
    case "yieldYard": return y < 21 ? "shelf" : x < 34 ? "ridge"
      : y >= 25 ? "melt" : y >= 23 ? "reedsDeep" : "reedsUpper";
    // Frontier: each zone is a northern and a southern patch either side of its kettle.
    case "frostfloor": return y < 7 ? "floe" : "drift";
    case "crystalCache": return y < 7 ? "geode" : "vault";
    case "candleDunes": return y < 20 ? "crest" : "flats";
    case "rugmire": return y < 20 ? "hummock" : "sink";
    case "magmaMint": return y < 20 ? "vents" : "flow";
    // Undernet: north / south of each zone's road.
    case "mempoolCaverns": return y < 35 ? "vein" : "grotto";
    case "neonGrid": return y < 35 ? "rack" : "subnet";
    case "coralLedger": return y < 45 ? "shallows" : "reef";
    case "cloudChain": return y < 45 ? "cirrus" : "nimbus";
    // Sidechain: north / south of each zone's road.
    case "halvingHarvest": return y < 45 ? "orchard" : "fields";
    case "ghostchain": return y < 35 ? "graves" : "crypts";
    case "pumpJungle": return y < 20 ? "canopy" : "understory";
    case "sakuraStake": return y < 6 ? "petals" : "koi";
    case "rugpullRuins": return y < 6 ? "colonnade" : "vault";
    default: return null;
  }
}

type BedMap = Partial<Record<PatchId, EncounterRow[]>>;

/**
 * Frontier and Undernet zones: the north patch keeps the young forms to catch, the south patch the
 * grown forms at the level of the zone's duels, so a crew can train where it fights.
 */
export const GROWN_SPLIT: Partial<Record<ZoneId, { north: PatchId; south: PatchId }>> = {
  candleDunes: { north: "crest", south: "flats" }, frostfloor: { north: "floe", south: "drift" },
  rugmire: { north: "hummock", south: "sink" }, crystalCache: { north: "geode", south: "vault" },
  magmaMint: { north: "vents", south: "flow" }, mempoolCaverns: { north: "vein", south: "grotto" },
  coralLedger: { north: "shallows", south: "reef" }, neonGrid: { north: "rack", south: "subnet" },
  cloudChain: { north: "cirrus", south: "nimbus" },
};
const carve = (table: EncounterRow[], beds: Partial<Record<PatchId, SpeciesId[]>>): BedMap =>
  Object.fromEntries(Object.entries(beds)
    .map(([patch, ids]) => [patch, table.filter(row => ids!.includes(row.id))]));

/**
 * Each route bed is its zone's one headline regular plus four or five locals that read as a
 * place: the cliff's quiet ledge is moon-gazers and sleepers, Chartwood's south strip is the
 * kitchen line, the Gulch's south beds are the feed. The one long bed left is Yield Yard's
 * two-tile reed shelf, which is deliberate -- it is the "rare forms" the Yield Yard sign
 * promises, and concentrating the mature tail there makes it huntable instead of diluting
 * it across every tile at one percent a face.
 */
export const ZONE_BEDS: Partial<Record<ZoneId, BedMap>> = {
  copeCliffs: carve(ZONES.copeCliffs.encounters, {
    scree: ["wojakle", "npcat", "midling", "rugfox", "seethtoad"],
    face: ["wojakle", "hopiumite", "gigachomp", "fomoling", "lurkit", "deluling"],
    ledge: ["wojakle", "moonling", "chillip", "snoozlet", "draftkit", "ghostkit"],
  }),
  chartwood: carve(ZONES.chartwood.encounters, {
    ticket: ["apeling", "chartgob", "mintmoth", "cloutling", "clipfinch"],
    grove: ["apeling", "grassnudge", "rugfox", "gatekit", "yapling"],
    hollowWest: ["apeling", "poutkit", "spoilpup", "crashpup", "depkit"],
    hollowEast: ["apeling", "hopiumite", "laserpup", "coneling", "puckup"],
    southStrip: ["apeling", "cookling", "syrupup", "crumbit"],
  }),
  gasGulch: carve(ZONES.gasGulch.encounters, {
    warmstones: ["gasgob", "chartgob", "rugfox", "chopperdog", "finekit"],
    southBeds: ["gasgob", "scrollmite", "pingling", "tabbit", "bufferret"],
    westEdge: ["gasgob", "laserpup", "lockkit", "receiptling"],
    eastEdge: ["gasgob", "slippeel", "hopiumite", "glazeling"],
  }),
  // The reed shelf is the only Yield grass north of the trial line, so it carries the gentle
  // Lv14-18 regulars the Cold Reed sign promises as "north grass" -- it is the bed a post-trial
  // crew drops back to. The melt edge is the far corner and takes the mature tail.
  yieldYard: carve(ZONES.yieldYard.encounters, {
    shelf: ["yieldyeti", "whalord", "chopperdog", "moonling", "slippeel", "bagsnail"],
    ridge: ["fomobeast", "airdropqueen", "diamondshell", "copiumax", "cloutking", "candlewyrm"],
    reedsUpper: ["bogbaron", "ratioking", "yapstorm", "threadmaw", "doomwyrm", "kilnmutt"],
    reedsDeep: ["lorelord", "gatewyrm", "glazewyrm", "nocapra", "lawnlord", "maldmaw"],
  }),
  // Frontier beds split the rare sightings so each patch is worth walking to on its own.
  frostfloor: carve(ZONES.frostfloor.encounters, {
    floe: ["frostbag", "pengwallet", "icepup", "yieldyeti", "cachemole"],
    drift: ["glacierbag", "diamondpaws", "coldvault", "vaultmole"],
  }),
  crystalCache: carve(ZONES.crystalCache.encounters, {
    geode: ["shardling", "prismpup", "nodelet", "cachemole"],
    vault: ["prismhound", "validrake", "shardking", "vaultmole", "genesiwyrm"],
  }),
  candleDunes: carve(ZONES.candleDunes.encounters, {
    crest: ["candlecub", "shillpion", "sprikle", "gasbull", "humpfund"],
    flats: ["wickwolf", "saguarocket", "shillscorp", "magmabull"],
  }),
  rugmire: carve(ZONES.rugmire.encounters, {
    hummock: ["ruglet", "mirebot", "botfly", "honeytoad", "bogbaron"],
    sink: ["swarmlord", "mireshill", "rugwraith", "honeybaron", "ursarug"],
  }),
  magmaMint: carve(ZONES.magmaMint.encounters, {
    vents: ["emberbit", "gasbull", "blazebit", "shillpion"],
    flow: ["magmabull", "moltwisp", "shillscorp", "infernomint"],
  }),
  mempoolCaverns: carve(ZONES.mempoolCaverns.encounters, {
    vein: ["pendbat", "shroomint", "hashgrub", "geodeggo", "cachemole"],
    grotto: ["stuckwing", "shroomancer", "asicarab", "geodrake"],
  }),
  coralLedger: carve(ZONES.coralLedger.encounters, {
    shallows: ["shrimpling", "sidecrab", "jellyfud", "slippeel"],
    reef: ["krillionaire", "crabstack", "medusell", "pearlclam"],
  }),
  neonGrid: carve(ZONES.neonGrid.encounters, {
    rack: ["chatbit", "glitchpup", "fanbit", "ledmoth"],
    subnet: ["chatlord", "glitchhound", "fanstorm", "rackmoth", "neuralith"],
  }),
  cloudChain: carve(ZONES.cloudChain.encounters, {
    cirrus: ["lunapup", "nimbaa", "rocketot", "craterling"],
    nimbus: ["lunawolf", "nimbram", "rocketitan", "selenarch"],
  }),
  // Sidechain: each rare final lives on one side of the road; legends sleep in the south beds.
  halvingHarvest: carve(ZONES.halvingHarvest.encounters, {
    orchard: ["pumpkoin", "crowlet", "nutling", "gourdian", "nutvault"],
    fields: ["pumpkoin", "crowlet", "nutling", "crowllateral"],
  }),
  ghostchain: carve(ZONES.ghostchain.encounters, {
    graves: ["rektling", "tombstonk", "webthree", "deadcat", "cryptkeep", "rektoplasm"],
    crypts: ["rektling", "tombstonk", "webthree", "deadcat", "arachnft", "catacomb"],
  }),
  pumpJungle: carve(ZONES.pumpJungle.encounters, {
    canopy: ["toucoin", "vinesnek", "slothlet", "apemajor", "beakfolio"],
    understory: ["toucoin", "vinesnek", "slothlet", "vineconda", "slothstake"],
  }),
  sakuraStake: carve(ZONES.sakuraStake.encounters, {
    petals: ["koinfish", "origamint", "sproutle", "craneprint", "ryukoin"],
    koi: ["koinfish", "origamint", "sproutle", "bonsaitoise", "arborealm"],
  }),
  rugpullRuins: carve(ZONES.rugpullRuins.encounters, {
    colonnade: ["golemint", "riddlekit", "wraplet", "tombstonk", "colosstake", "sphynxfi"],
    vault: ["golemint", "riddlekit", "wraplet", "wrappharaoh", "rektquiem"],
  }),
};

// The melt edge is whatever the other four Yield beds do not carry, so the zone stays whole by
// construction rather than by a list somebody has to remember to extend.
ZONE_BEDS.yieldYard!.melt = ZONES.yieldYard.encounters
  .filter(row => !Object.values(ZONE_BEDS.yieldYard!).some(bed => bed.includes(row)));

/**
 * Post-trial the same patches answer with grown forms, and two of them grow up in place:
 * Chartwood's sulk hollow becomes Gravylord, Leakmaw, Crashmaw and Depbaron -- the evolutions
 * of the four kits that sulk there before the trial -- and its kitchen strip becomes Cookwyrm,
 * Sapreme and Breadwinner. The trial changes what the grass holds, not where it holds it.
 */
export const VETERAN_BEDS: Partial<Record<ZoneId, BedMap>> = {
  chartwood: carve(VETERAN_ENCOUNTERS.chartwood!, {
    hollowWest: ["gravylord", "leakmaw", "crashmaw", "depbaron", "postwyrm", "candlewyrm"],
    hollowEast: ["laserhound", "apemajor", "cloutking", "airdropqueen", "yapstorm"],
    southStrip: ["cookwyrm", "sapreme", "breadwinner", "lawnlord", "gatewyrm"],
  }),
  gasGulch: carve(VETERAN_ENCOUNTERS.gasGulch!, {
    southBeds: ["doomwyrm", "pinglord", "tabalanche", "lagomorph", "cliplord", "clapback"],
    westEdge: ["locklord", "auditlord", "rugwolf", "gasgob", "maldmaw", "detourant"],
    eastEdge: ["copiumax", "glazewyrm", "chopperalpha", "kilnmutt", "candlewyrm"],
  }),
  yieldYard: carve(VETERAN_ENCOUNTERS.yieldYard!, {
    reedsUpper: ["yieldyeti", "diamondshell", "moonlord", "fomobeast", "bogbaron", "chillossus"],
    reedsDeep: ["threadmaw", "ratioking", "linebaron", "midbaron", "ghostwyrm"],
    ridge: ["lorelord", "delulord", "ehvergreen", "napocalypse", "honkference"],
    melt: ["tuquelord", "vibewyrm", "nocapra", "saucelord", "slaylord"],
  }),
};

/**
 * The rows a tall-grass step at (x, y) can meet. Pass the time of day to add that period's
 * visitors (secrets.ts); daytime and callers without a period get the plain table.
 */
export function encountersAt(x: number, y: number, canopyCleared = false, period?: DayPeriod): EncounterRow[] {
  const zone = zoneAt(x, y);
  const visitors = period && period !== "day" ? TIME_ROWS[zone]?.[period] ?? [] : [];
  if (zone === "drippond") {
    const rows = x < 22 ? DRIPPOND_BEDS.west : y < 9 ? DRIPPOND_BEDS.northeast : DRIPPOND_BEDS.southeast;
    return visitors.length ? [...rows, ...visitors] : rows;
  }
  const veteran = canopyCleared && y >= 21 ? VETERAN_ENCOUNTERS[zone] : undefined;
  const patch = patchAt(zone, x, y);
  const bed = patch ? (veteran ? VETERAN_BEDS : ZONE_BEDS)[zone]?.[patch] : undefined;
  const rows = bed ?? veteran ?? ZONES[zone].encounters;
  return visitors.length ? [...rows, ...visitors] : rows;
}

/** Zone and grass bed of a tile (legend lairs are beds). */
export function bedAt(x: number, y: number): { zone: ZoneId; bed: PatchId | null } {
  const zone = zoneAt(x, y);
  return { zone, bed: patchAt(zone, x, y) };
}

export type HabitatRegion = "all" | "north" | "south";

/**
 * A bed means a face is met in a patch, not across a whole zone, and the trial swaps what the
 * southern patches hold. The Field Guide therefore cannot read the zone catalog and call it a
 * habitat -- post-trial, ten of Chartwood's twenty faces live only in grass the trial has already
 * turned veteran. This is the honest answer, walked once from the map per trial state: the rows a
 * tall tile really rolls, tagged with the region label the guide prints.
 */
function walkHabitats(canopyCleared: boolean): ReadonlySet<string> {
  const keys = new Set<string>();
  for (let y = 0; y < MAP_H; y++) for (let x = 0; x < MAP_W; x++) {
    if (!tileAt(x, y).tall) continue;
    const zone = zoneAt(x, y);
    const region: HabitatRegion = canopyCleared && VETERAN_ENCOUNTERS[zone] ? (y >= 21 ? "south" : "north") : "all";
    for (const row of encountersAt(x, y, canopyCleared)) keys.add(`${row.id}:${zone}:${region}:${row.lo}:${row.hi}`);
  }
  return keys;
}

const HABITATS = { open: walkHabitats(false), cleared: walkHabitats(true) };

/** True when some tall tile in that zone and region really offers this row. */
export function habitatReachable(
  id: SpeciesId, zone: ZoneId, region: HabitatRegion, lo: number, hi: number, canopyCleared: boolean,
): boolean {
  return HABITATS[canopyCleared ? "cleared" : "open"].has(`${id}:${zone}:${region}:${lo}:${hi}`);
}

export function tileAt(x: number, y: number): TileDef {
  if (y < 0 || x < 0 || y >= MAP_H || x >= MAP_W) return TILES.void;
  const id = MAP[y]?.[x];
  return id ? TILES[id] : TILES.void;
}

export function spotAt(x: number, y: number): Spot | undefined {
  return SPOTS.find((s) => s.x === x && s.y === y);
}

export function spotFacing(px: number, py: number, dx: number, dy: number): Spot | undefined {
  return spotAt(px + dx, py + dy) ?? spotAt(px, py);
}
